/**
 * MY Allotment of Shares (Cash) — M32200 mapper.
 *
 * Variable Map 출처: `Allotment_Shares_Cash_Variable_Map.md`.
 * 11개 문서 패키지의 공통/문서별 변수를 templates 가 사용할 형식으로 변환.
 *
 * 통일 결정이 아직 안 된 항목 — skeleton 단계에서는 양쪽 키 모두 출력 (호환 안전):
 *   - cosec_ (1/6/10) vs secretary_ / lodger_ (9)
 *   - allottee.shares (4/6/7) vs allottee.share_count (11)
 *   - 날짜군 (dcr_date, letter_date, allotment_date, signature_date, circulation_date, deadline_date)
 *
 * 추후 확정 후 단일 키로 정리.
 */

/** Governing document type — DCR 의 resolution_basis_* 분기 (AOD/AGM/Change Address 와 동일). */
export type GoverningDocumentType = 'constitution' | 'articles' | 'third_schedule';

export interface DirectorInput {
  contactCd?: string;
  name: string;
  email?: string;
  nricNo?: string;
  identificationNo?: string;
  dateOfAppointment?: string;
}
export interface CosecInput {
  contactCd?: string;
  name: string;
  email?: string;
  address?: string;
  licenseNo?: string;
  nricNo?: string;
  ssmCertNo?: string;
  firm?: string;
  companyName?: string;
  phone?: string;
  /** Contact 의 주소 후보 (residential / service / business / branch) — chip 으로 노출. */
  addresses?: Array<{ value: string; type: string; label?: string }>;
  /** 현재 선택된 주소 chip index — UI highlight 용. */
  addressSelectedIdx?: number;
}
export interface AllotteeInput {
  contactCd?: string;
  name: string;
  address?: string;
  idNo?: string;
  /** 이번 발행 수량 (=신주 배정 수량) */
  shares: number;
  /** 주식 종류 (Ordinary 등) — 비어있으면 'Ordinary' default */
  shareType?: string;
  /** Contact 의 주소 후보 — chip 으로 노출. */
  addresses?: Array<{ value: string; type: string; label?: string }>;
  addressSelectedIdx?: number;

  // ── 청약자(Subscriber) 상세 — Allottee 와 동일인이므로 같은 row 에 보유. Subscription 문서(2/3)용. ──
  /** 개인/법인 — Subscription Individual/Corporate 분기 (B09001 Corporate / B09002 Individual) */
  type?: 'individual' | 'corporate' | '';
  occupation?: string;
  dob?: string;
  nationality?: string;
  race?: string;
  // PEP
  isPolitician?: boolean; politicianDetails?: string;
  isPepConnected?: boolean; pepConnectionDetails?: string;
  holdsGovtPosition?: boolean; govtPositionDetails?: string;
  // Trustee/BO
  isTrustee?: boolean;
  boFullName?: string; boRegistrationNo?: string; boNature?: string;
  boPlace?: string; boRegisteredAddress?: string; boBusinessAddress?: string;
  // 청약 주식 수 — Allotted(shares)에서 자동 채워지되 편집 가능 (offered/subscribed/accepted)
  sharesOffered?: number; sharesSubscribed?: number; sharesAccepted?: number;
  appendixRef?: string;
}
export interface SubscriberInput {
  name: string;
  /** 'individual' | 'corporate' */
  type: 'individual' | 'corporate' | '';
  id?: string;
  dob?: string;
  address?: string;
  nationality?: string;
  race?: string;
  occupation?: string;
  // PEP 분기 정보
  isPolitician?: boolean;
  politicianDetails?: string;
  isPepConnected?: boolean;
  pepConnectionDetails?: string;
  holdsGovtPosition?: boolean;
  govtPositionDetails?: string;
  // Trustee/Nominee — true 면 BO(수익소유자) 공개 섹션 노출 ([[if is_trustee]])
  isTrustee?: boolean;
  // BO
  boFullName?: string;
  boRegistrationNo?: string;
  boNature?: string;
  boPlace?: string;
  boRegisteredAddress?: string;
  boBusinessAddress?: string;
  // 주식 수
  sharesOffered?: number;
  sharesSubscribed?: number;
  sharesAccepted?: number;
  appendixRef?: string;
}
export interface LodgerInput {
  name?: string;
  nric?: string;
  address?: string;
  phone?: string;
  email?: string;
}

export interface AllotmentSharesInput {
  // 회사
  companyName: string;
  companyRegistrationNo: string;
  companyAddress?: string;
  constitutionClauseNo?: string;
  hasConstitution: boolean;
  // ── DCR 거버닝-문서 모델 (AOD/Change Address 와 동일) ──
  governingDocumentType?: GoverningDocumentType;  // 기본 'constitution'
  constitutionClauseNumber?: string;              // constitution_clause_number (없으면 constitutionClauseNo 폴백)
  directorCount?: 'single' | 'multiple';          // is_sole_director_company / is_multiple_director_company
  memberCount?: 'single' | 'multiple';            // is_sole_member / is_multiple_member (MWR, 패널 수동 선택)
  hasCommonSeal?: boolean;                         // has_common_seal / no_common_seal (패널 토글)
  // 비서
  cosec: CosecInput;
  // 이사 / 주주(allottees)
  directors: DirectorInput[];
  allottees: AllotteeInput[];
  // 청약자 (Individual + Corporate 각각)
  subscribers: SubscriberInput[];
  // Lodger (Section 76 Secretary 제출인) — 비서와 다른 경우만 입력
  lodger?: LodgerInput;
  // 발행 정보
  sharePrice: number;
  totalNewShares: number;
  totalAmount: number;
  totalAmountWords?: string;
  paidUpCapitalBefore?: number;
  paidUpCapitalAfter?: number;
  // 날짜군 — 고객 입력 2개(resolutionDate, allotmentDate), 나머지는 패널에서 파생
  resolutionDate?: string;
  dcrDate?: string;
  circulationDate?: string;
  deadlineDate?: string;
  letterDate?: string;
  allotmentDate?: string;
  signatureDate?: string;
  eventDate?: string;
  tarikhDokumen?: string;
  periodStartDate?: string;
  periodEndDate?: string;
  // 조건
  grantOfRights?: boolean;
  hasConditions?: boolean;
  conditionsText?: string;
  multipleMembers?: boolean;
  // Section 78 — Entity Register 의 기존 발행 주식 수(이번 배정 전) — Total Current / Accumulated 계산용
  currentIssuedShares?: number;
  // 기타
  submissionReference?: string;
}

const norm = (v: any) => String(v ?? '').trim();

/** 11개 문서 패키지 — 각 문서 포함 여부 + 분기 (SKILL.md 표준 detectXxxDocFlags 패턴). */
export interface AllotmentSharesDocFlags {
  /** 1. Authority to Issue Shares (DCR + MWR) */
  hasAuthorityToIssue: boolean;
  /** 2. Subscription for New Shares (Individual) */
  hasSubscriptionIndividual: boolean;
  /** 3. Subscription for New Shares (Corporate) */
  hasSubscriptionCorporate: boolean;
  /** 4. Waiver of Preemption Rights */
  hasWaiverPreemption: boolean;
  /** 5. Pricing for Allotment of Shares */
  hasPricing: boolean;
  /** 6. Letter of Allotment */
  hasLetterOfAllotment: boolean;
  /** 7. Increase of Paid-Up Capital */
  hasIncreasePaidUp: boolean;
  /** 8. Section 76 Notice (Director) */
  hasSection76Director: boolean;
  /** 9. Section 76 Notice (Secretary) */
  hasSection76Secretary: boolean;
  /** 10. ROC Cover Letter (Section 76) */
  hasRocCoverLetter: boolean;
  /** 11. Section 78 Return of Allotment (Director Sign) */
  hasSection78Return: boolean;
  /** subscriber/PEP/BO 섹션 노출 — 2/3 중 하나라도 있으면 */
  hasAnySubscription: boolean;
  /** allottee 반복 블록 섹션 노출 — 4/6/7/11 중 하나라도 있으면 */
  hasAnyAllottee: boolean;
}

const any = (re: RegExp, docs: Array<{ templateName?: string; docCode?: string }>) =>
  docs.some((d) => re.test(String(d?.templateName || '')));

export function detectAllotmentSharesDocFlags(
  documents: Array<{ templateName?: string; docCode?: string }>,
): AllotmentSharesDocFlags {
  const docs = documents || [];
  const f = {
    hasAuthorityToIssue:     any(/authority\s*to\s*issue|issue\s*shares.*(dcr|mwr)/i, docs),
    hasSubscriptionIndividual: any(/subscription.*(individual|natural\s*person)/i, docs),
    hasSubscriptionCorporate:  any(/subscription.*(corporate|company|body\s*corporate)/i, docs),
    hasWaiverPreemption:     any(/waiver.*preemption|preemption.*rights/i, docs),
    hasPricing:              any(/pricing|price.*allotment/i, docs),
    hasLetterOfAllotment:    any(/letter\s*of\s*allotment/i, docs),
    hasIncreasePaidUp:       any(/increase.*paid[\s\-_]*up|paid[\s\-_]*up.*capital/i, docs),
    hasSection76Director:    any(/section\s*76.*director|s\.?\s*76.*director/i, docs),
    hasSection76Secretary:   any(/section\s*76.*(secretary|lodger)|s\.?\s*76.*(secretary|lodger)/i, docs),
    hasRocCoverLetter:       any(/roc.*cover|cover\s*letter.*(section\s*76|s\.?\s*76)/i, docs),
    hasSection78Return:      any(/section\s*78|return\s*of\s*allotment/i, docs),
  };
  return {
    ...f,
    hasAnySubscription: f.hasSubscriptionIndividual || f.hasSubscriptionCorporate,
    hasAnyAllottee: f.hasWaiverPreemption || f.hasLetterOfAllotment || f.hasIncreasePaidUp || f.hasSection78Return,
  };
}

/**
 * 11개 문서가 사용할 변수 집합을 단일 record 로 변환.
 *   - 같은 의미의 다중 키 모두 출력 (cosec_/secretary_ 등) → 통일 결정 후 정리.
 *   - allottees 는 mapper 단계에서 contactCd 기준 dedupe + shares 합산 (M32101 incorporation post 와 동일 패턴).
 */
export function buildAllotmentSharesVariables(input: AllotmentSharesInput): Record<string, any> {
  const cosec = input.cosec || ({} as CosecInput);
  const primaryDirector = input.directors?.[0] || ({ name: '' } as DirectorInput);
  const lodger = input.lodger || {};

  // allottee dedupe + shares 합산 (Incorporation Post 와 동일 패턴)
  const allotteesMap = new Map<string, AllotteeInput>();
  for (const a of input.allottees || []) {
    const cc = norm(a.contactCd);
    const nm = norm(a.name);
    if (!nm) continue;
    const key = cc || `name:${nm.toUpperCase()}`;
    const sh = Number(a.shares || 0) || 0;
    const existing = allotteesMap.get(key);
    if (existing) {
      existing.shares += sh;
    } else {
      allotteesMap.set(key, { ...a, contactCd: cc, name: nm, shares: sh });
    }
  }
  const allottees = Array.from(allotteesMap.values());

  // 반복 블록 row builder.
  //   ⚠ documentVariableReplacer 의 [[if allottees]] 반복부는 item 객체의 "키 = 플레이스홀더 전체 이름" 으로
  //     [[key]] 를 치환한다 (prefix 자동 부여 없음). 따라서 row 키는 템플릿 변수명과 정확히 일치해야 한다.
  //   신 템플릿(underscore: [[allottee_name]]) + 구 변수맵(dot: [[allottee.name]]) 양쪽 키 모두 출력.
  const buildAllotteeRow = (a: AllotteeInput, idx: number): Record<string, string> => {
    const no = String(idx + 1);
    const name = a.name || '';
    const address = a.address || '';
    const idNo = a.idNo || '';
    const shares = String(a.shares || 0);
    const shareType = a.shareType || 'Ordinary';
    return {
      allottee_no: no, 'allottee.no': no,
      allottee_name: name, 'allottee.name': name,
      allottee_address: address, 'allottee.address': address,
      allottee_id_no: idNo, 'allottee.id_no': idNo,
      allottee_shares: shares, 'allottee.shares': shares,           // 4/6/7
      allottee_share_count: shares, 'allottee.share_count': shares, // 11
      allottee_share_type: shareType, 'allottee.share_type': shareType,
    };
  };
  const allotteeRows = allottees.map((a, idx) => buildAllotteeRow(a, idx));

  // ── Section 78 Share Details — allottee 를 주식 종류(class)별로 묶어 자동 생성 ──
  //   row 키는 [[share_type]]/[[share_details]]/[[share_number]]/[[share_price_per_share]]/[[share_total_issued]] 와 일치 (+ dot 호환).
  const pricePerShare = Number(input.sharePrice || 0) || 0;
  const byClass = new Map<string, number>();
  for (const a of allottees) {
    const cls = norm(a.shareType) || 'Ordinary';
    byClass.set(cls, (byClass.get(cls) || 0) + (Number(a.shares || 0) || 0));
  }
  const shareRows: Array<Record<string, string>> = Array.from(byClass.entries()).map(([cls, num], idx) => {
    const totalIssued = num * pricePerShare;
    return {
      share_no: String(idx + 1), 'share.no': String(idx + 1),
      share_number: String(num), 'share.number': String(num),
      share_type: cls, 'share.type': cls,
      share_details: cls + ' shares', 'share.details': cls + ' shares',
      share_price_per_share: String(pricePerShare), 'share.price_per_share': String(pricePerShare),
      share_total_issued: String(totalIssued), 'share.total_issued': String(totalIssued),
    };
  });

  // ── Section 78 합계 ──
  //   Additional = 이번 배정 신주 수(현금) / Current = Entity Register 기존 발행 주식 수 / Accumulated = Current + Additional
  const additionalShares = Number(input.totalNewShares || 0) || 0;
  const currentShares = Number(input.currentIssuedShares || 0) || 0;
  const accumulatedShares = currentShares + additionalShares;

  // ── 거버닝-문서 분기 (AOD/Change Address 동일) ──
  const govType: GoverningDocumentType = input.governingDocumentType || 'constitution';
  const stripClause = (v: string) => {
    const s = norm(v);
    if (!s) return '';
    const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
    if (m1) return m1[1];
    const m2 = s.match(/\b(\d+[\w()]*)/);
    return m2 ? m2[1] : s;
  };
  const constitutionClauseNumber = govType === 'third_schedule'
    ? ''
    : (stripClause(input.constitutionClauseNumber || '') || stripClause(input.constitutionClauseNo || ''));
  const isSingleDirector = input.directorCount === 'single';
  const isSingleMember = input.memberCount === 'single';
  const resolutionBasisClause =
    govType === 'third_schedule'
      ? 'Paragraph 15 of the Third Schedule of the Companies Act 2016'
      : govType === 'articles'
        ? (constitutionClauseNumber
            ? `Article ${constitutionClauseNumber} of the Company's Constitution (Articles of Association)`
            : "the Company's Constitution (Articles of Association)")
        : (constitutionClauseNumber
            ? `Clause ${constitutionClauseNumber} of the Company's Constitution`
            : "the Company's Constitution");

  return {
    // === 회사 ===
    company_name: input.companyName,
    company_registration_no: input.companyRegistrationNo,
    company_address: input.companyAddress || '',
    constitution_clause_no: constitutionClauseNumber || (input.constitutionClauseNo || ''),
    has_constitution: govType !== 'third_schedule' ? 'true' : '',

    // === DCR 거버닝-문서 분기 (3개 문서 공통) ===
    //   [[if resolution_basis_constitution]]Clause [[constitution_clause_number]] of the Company's Constitution[[endif]]
    //   [[if resolution_basis_articles]]Article ... (Articles of Association)[[endif]]
    //   [[if resolution_basis_third_schedule]]Paragraph 15 of the Third Schedule ...[[endif]]
    constitution_clause_number: constitutionClauseNumber,
    resolution_basis_constitution: govType === 'constitution',
    resolution_basis_articles: govType === 'articles',
    resolution_basis_third_schedule: govType === 'third_schedule',
    resolution_basis_clause: resolutionBasisClause,
    governing_document_clause: resolutionBasisClause,
    // [[if is_sole_director_company]] / [[if is_multiple_director_company]]
    is_sole_director_company: isSingleDirector,
    is_multiple_director_company: !isSingleDirector,
    // [[if is_sole_member]] / [[if is_multiple_member]] (Authority to Issue Shares MWR)
    is_sole_member: isSingleMember,
    is_multiple_member: !isSingleMember,
    // [[if has_common_seal]] / [[if no_common_seal]] (Increase of Paid-Up Capital)
    has_common_seal: !!input.hasCommonSeal,
    no_common_seal: !input.hasCommonSeal,

    // === 비서 (양쪽 키 모두 — 통일 결정 후 정리) ===
    cosec_name: cosec.name || '',
    cosec_address: cosec.address || '',
    cosec_phone: cosec.phone || '',
    cosec_registration_no: cosec.licenseNo || '',
    cosec_ssm_cert_no: cosec.ssmCertNo || '',
    // Section 76 Secretary (9) 호환
    secretary_name: cosec.name || '',
    secretary_license_no: cosec.licenseNo || '',
    secretary_sspc_no: cosec.ssmCertNo || '',

    // === Lodger (Section 76 Secretary 제출인) — 비어있으면 cosec fallback ===
    lodger_name: lodger.name || cosec.name || '',
    lodger_nric: lodger.nric || cosec.nricNo || '',
    lodger_address: lodger.address || cosec.address || '',
    lodger_phone: lodger.phone || cosec.phone || '',
    lodger_email: lodger.email || '',

    // === 이사 (Section 78 — 직접 텍스트 서명) ===
    director_name: primaryDirector.name || '',
    director_nric: primaryDirector.nricNo || primaryDirector.identificationNo || '',

    // === 주식 정보 ===
    share_price: String(input.sharePrice || ''),
    total_new_shares: String(input.totalNewShares || ''),
    total_amount: String(input.totalAmount || ''),
    total_amount_words: input.totalAmountWords || '',
    paid_up_capital_before: String(input.paidUpCapitalBefore || ''),
    paid_up_capital_after: String(input.paidUpCapitalAfter || ''),

    // === 날짜군 (모든 키 출력 — UI 에서 2개 입력 → 패널이 파생) ===
    resolution_date: input.resolutionDate || input.dcrDate || '',
    dcr_date: input.dcrDate || '',
    circulation_date: input.circulationDate || '',
    deadline_date: input.deadlineDate || '',
    letter_date: input.letterDate || '',
    allotment_date: input.allotmentDate || '',
    signature_date: input.signatureDate || '',
    event_date: input.eventDate || '',
    // ROC Cover Letter (Section 76) 의 [[tarikh_dokumen]] = 문서 일자 → Resolution Date 로 매핑.
    tarikh_dokumen: input.tarikhDokumen || input.resolutionDate || input.dcrDate || '',
    // Section 78 "between the [[period_start_date]] and [[period_end_date]]" —
    //   값 resolve(배정일 기본 / skip 시 blank)는 패널에서 처리 → mapper 는 받은 값 그대로 출력.
    period_start_date: input.periodStartDate || '',
    period_end_date: input.periodEndDate || '',

    // === 조건 분기 ===
    grant_of_rights: input.grantOfRights ? 'true' : '',
    has_conditions: input.hasConditions ? 'true' : '',
    multiple_members: input.multipleMembers ? 'true' : '',
    conditions_text: input.conditionsText || '',

    // === 기타 ===
    submission_reference: input.submissionReference || '',
    appendix_ref: input.subscribers?.[0]?.appendixRef || '',

    // === 반복 블록 (allottee) — 문서 4/6/7/11 ===
    //   신 마커 [[if allottees]] (plural) / 구 마커 [[if allottee]] (singular) 모두 대응 — 같은 rows 를 양쪽 키로 출력.
    allottee: allotteeRows,
    allottees: allotteeRows,

    // === Subscriber 정보 (문서 2/3) — 첫 청약자 기준 평탄화. 다중 청약자는 추후 반복 블록 검토 ===
    ...(input.subscribers?.[0]
      ? (() => {
          const s = input.subscribers[0];
          return {
            subscriber_name: s.name,
            subscriber_id: s.id || '',
            subscriber_dob: s.dob || '',
            subscriber_address: s.address || '',
            subscriber_nationality: s.nationality || '',
            subscriber_race: s.race || '',
            subscriber_occupation: s.occupation || '',
            subscriber_type: s.type || '',
            // 주식 수
            shares_offered: String(s.sharesOffered || ''),
            shares_subscribed: String(s.sharesSubscribed || ''),
            shares_accepted: String(s.sharesAccepted || ''),
            // BO
            bo_full_name: s.boFullName || '',
            bo_registration_no: s.boRegistrationNo || '',
            bo_nature: s.boNature || '',
            bo_place: s.boPlace || '',
            bo_registered_address: s.boRegisteredAddress || '',
            bo_business_address: s.boBusinessAddress || '',
            // PEP
            is_politician: s.isPolitician ? 'true' : '',
            politician_details: s.politicianDetails || '',
            is_pep_connected: s.isPepConnected ? 'true' : '',
            pep_connection_details: s.pepConnectionDetails || '',
            holds_govt_position: s.holdsGovtPosition ? 'true' : '',
            govt_position_details: s.govtPositionDetails || '',
            // Trustee/Nominee → BO(수익소유자) 공개. 문서별 변수명 다름:
            //   Individual(2): [[if is_trustee]] / Corporate(3): [[if has_bo]] / [[if !has_bo]] — 둘 다 출력.
            is_trustee: s.isTrustee ? 'true' : '',
            has_bo: s.isTrustee ? 'true' : '',
          };
        })()
      : {}),

    // === Section 78 — share 명세 반복 블록 ([[if shares]] / [[if share]]) — skeleton: 빈 배열, 추후 채움 ===
    //   채울 때 row 키는 [[share_type]]/[[share_details]]/[[share_number]]/[[share_price_per_share]]/[[share_total_issued]] 와 일치해야 함.
    share: shareRows,
    shares: shareRows,

    // === Section 78 합계 (자동 계산) ===
    //   현금 배정이므로 cash = 전체, otherwise = 0.
    total_current: String(currentShares),
    total_current_cash: String(currentShares),
    total_current_otherwise: '0',
    total_additional: String(additionalShares),
    total_additional_cash: String(additionalShares),
    total_additional_otherwise: '0',
    total_accumulated: String(accumulatedShares),
    total_accumulated_cash: String(accumulatedShares),
    total_accumulated_otherwise: '0',

    // === Raw 정보 (backend register-allotment-shares 호환을 위해 보존 — Incorporation Post 와 동일 패턴) ===
    __cosecsRaw: [{ ...cosec }],
    __directorsRaw: (input.directors || []).map((d) => ({
      contactCd: norm(d.contactCd),
      name: norm(d.name),
      email: norm(d.email),
      nricNo: norm(d.nricNo) || norm(d.identificationNo),
      dateOfAppointment: norm(d.dateOfAppointment),
    })),
    __allotteesRaw: allottees.map((a) => ({
      contactCd: a.contactCd,
      name: a.name,
      shares: a.shares,
    })),
  };
}
